import type {
  AllTaxonomyConceptsResult,
  CardBlogResult,
  PageResourceCenterIndexResult,
} from '~/sanity/types';
import { useDebounceFn } from '@vueuse/core';
import SolrQuery from '~/utils/solr/SolrQuery';
import SolrFq from '~/utils/solr/SolrFq';
import SolrSort from '~/utils/solr/SorlSort';

type TopLevelFilters = NonNullable<NonNullable<PageResourceCenterIndexResult>['filters']>;

export type SupportedContentType =
  | 'blog'
  | 'spotlight'
  | 'techTip'
  | 'caseStudy'
  | 'video'
  | 'whatIsNew'
  | 'lumaEvent'
  | 'genericEvent'
  | 'linkedEvent';

export type CardTaxonomies = NonNullable<NonNullable<CardBlogResult>['taxonomies']>;
// import card data types and add them to the list in `OneOf`
export type PaginatedFilterResult<T extends OneOf<[NonNullable<CardBlogResult[]>]>> = {
  items: T;
  total: number;
  page: number;
  limit: number;
};

export type TaxonomyFilterOptionsConfig = Array<{
  id: string;
  label: string;
  slug: string;
  children: Array<{
    id: string;
    label: string;
    slug: string;
    children: Array<{
      id: string;
      label: string;
      slug: string;
    }>;
  }>;
}>;

export type FilterOption = {
  id: string;
  label: string;
  parentId: string;
  parentLabel: string;
};

export type FilterOptionsResponse = Array<FilterOption>;

/** the key is the parent slug and the value is an array of slugs */
type TaxonomyOptionsChangeEvent = Record<string, Array<string>>;

/** Map from taxonomy slug to boolean for checked */
export type TaxonomyCheckedStates = Record<string, boolean>;

export type PageChangedPayload = {
  pageNumber: number;
};

export type TaxonomyChangedPayload = {
  id: string;
  slug: string;
  isChecked: boolean;
  forceScroll?: boolean;
};

export type TaxonomyPill = {
  id: string;
  label: string;
  slug: string;
};

type SlugMapValue = {
  id: string;
  label: string;
  slug: string;
  groupSlug: string;
};

const { toTopOfWindow } = useWwwScroll();

const isEventOnlyContent = (contentType: string[]): boolean => {
  if (contentType.length === 0) {
    return false;
  }
  // check that all content types are event types
  if (contentType.every((type) => ['lumaEvent', 'genericEvent', 'linkedEvent'].includes(type))) {
    return true;
  }
  return false;
};

const getPagination = async ({
  pageNumber,
  pageLimit,
  contentTypes,
  taxonomies,
  sortDirection,
}: {
  pageNumber: number;
  pageLimit: number;
  contentTypes: Array<string>;
  taxonomies?: TaxonomyOptionsChangeEvent;
  sortDirection: 'asc' | 'desc';
}): Promise<PaginatedFilterResult<NonNullable<CardBlogResult>[]>> => {
  const fq = SolrFq.or().pushGroup('_type_s', contentTypes);

  if (isEventOnlyContent(contentTypes)) {
    fq.excludePastDates('articleDate_dt');
  }

  const taxes = Object.entries(taxonomies || {});
  for (const entry of taxes) {
    const [, slugs] = entry;
    fq.pushGroup('taxonomy_ss', slugs);
  }
  const q = SolrQuery.resourceCenter()
    .setFq(fq)
    .setSort(SolrSort.articleDate({ order: sortDirection }))
    .setRange({
      start: (pageNumber - 1) * pageLimit,
      rows: pageLimit,
    });
  const result = await useSolrQuery<CardBlogResult>({
    q,
  });

  const items = result.data.value;
  return {
    items,
    limit: pageLimit,
    page: pageNumber,
    total: result.numFound.value,
  };
};

const getFilterOptionsConfig = async ({
  // contentTypes,
  allTaxonomyConcepts,
  topLevelFilters,
}: {
  // contentTypes: Array<string>;
  allTaxonomyConcepts: NonNullable<AllTaxonomyConceptsResult>;
  topLevelFilters: TopLevelFilters;
}): Promise<TaxonomyFilterOptionsConfig> => {
  const conf: TaxonomyFilterOptionsConfig = [];
  topLevelFilters.forEach((topLevelFilter) => {
    const topLevelConcept = allTaxonomyConcepts.find(
      (concept) => concept.slug && concept.slug.current === topLevelFilter.slug
    );

    if (!topLevelConcept) {
      console.warn(`Top level filter ${topLevelFilter} not found in allTaxonomyConcepts`);
      return;
    }

    conf.push({
      id: topLevelConcept._id,
      label: topLevelConcept.prefLabel,
      slug: topLevelConcept.slug.current,
      children: topLevelConcept.children.map((child) => ({
        id: child._id,
        label: child.prefLabel,
        slug: child.slug.current,
        children: child.children.map((grandChild) => ({
          id: grandChild._id,
          label: grandChild.prefLabel,
          slug: grandChild.slug.current,
        })),
      })),
    });
  });

  return conf;
};

/** build map of checkable slugs to their group slug */
const buildSlugMap = (
  allTaxonomyConcepts: NonNullable<AllTaxonomyConceptsResult>
): Map<string, SlugMapValue> => {
  const slugMap = new Map<string, SlugMapValue>();
  for (const topLevelConcept of allTaxonomyConcepts) {
    if (!topLevelConcept.slug) {
      console.warn('Top level concept has no slug', topLevelConcept);
      continue;
    }
    for (const child of topLevelConcept.children) {
      for (const grandChild of child.children) {
        if (!grandChild.slug) {
          console.warn('Grandchild has no slug', grandChild);
          continue;
        }
        slugMap.set(grandChild.slug.current, {
          id: grandChild._id,
          label: grandChild.prefLabel,
          slug: grandChild.slug.current,
          groupSlug: topLevelConcept.slug.current,
        });
      }

      if (child.children.length === 0) {
        if (!child.slug) {
          console.warn('Child has no slug', child, 'parent', topLevelConcept);
          continue;
        }
        slugMap.set(child.slug.current, {
          id: child._id,
          label: child.prefLabel,
          slug: child.slug.current,
          groupSlug: topLevelConcept.slug.current,
        });
      }
    }
  }

  return slugMap;
};

const getFiltersFromQuery = (slugMap: Map<string, SlugMapValue>): TaxonomyOptionsChangeEvent => {
  const event: TaxonomyOptionsChangeEvent = {};
  if (!window) {
    return event;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const slugs = urlParams.getAll('f');
  for (const slug of slugs) {
    if (!slugMap.has(slug)) {
      console.warn('No slug found for', slug);
      continue;
    }
    const slugMapValue = slugMap.get(slug)!;
    const groupSlug = slugMapValue.groupSlug;
    if (!event[groupSlug]) {
      event[groupSlug] = [];
    }
    event[groupSlug]!.push(slug);
  }
  console.log('getFiltersFromQuery', { slugs, event });

  return event;
};

export const useContentFilter = async ({
  contentType,
  allTaxonomyConcepts,
  topLevelFilters,
  sortDirection,
}: {
  contentType: Array<SupportedContentType> | SupportedContentType;
  allTaxonomyConcepts: NonNullable<AllTaxonomyConceptsResult>;
  topLevelFilters: TopLevelFilters;
  sortDirection: 'asc' | 'desc';
}) => {
  const router = useRouter();

  const contentTypes = Array.isArray(contentType) ? contentType : [contentType];

  const slugMap = buildSlugMap(allTaxonomyConcepts);
  const filtersFromQuery = getFiltersFromQuery(slugMap);
  const initialTaxonomyCheckedStates: TaxonomyCheckedStates = {};
  Object.values(filtersFromQuery).forEach((slugs) => {
    slugs.forEach((slug) => {
      initialTaxonomyCheckedStates[slug] = true;
    });
  });

  const initPage = useRoute().query.page ? parseInt(useRoute().query.page as string, 10) : 1;
  const pageNumber = ref(initPage);
  const pageLimit = ref(15);
  const isLoading = ref(false);
  const taxonomyCheckedStates = ref<TaxonomyCheckedStates>(initialTaxonomyCheckedStates);
  const taxonomyOptionsConfig = ref(
    await getFilterOptionsConfig({
      // contentTypes,
      allTaxonomyConcepts,
      topLevelFilters,
    })
  );

  const paginationResult = ref(
    await getPagination({
      taxonomies: filtersFromQuery,
      pageNumber: pageNumber.value,
      pageLimit: pageLimit.value,
      contentTypes,
      sortDirection,
    })
  );
  // set in onTaxonomyChange or onPageChange to force scroll to top of page
  const forceScroll = ref(false);

  let lastRunFilters: TaxonomyOptionsChangeEvent | null = null;
  const runFilter = async (changeEvent: TaxonomyOptionsChangeEvent): Promise<void> => {
    isLoading.value = true;
    if (lastRunFilters) {
      // compare changeEvent with lastRunFilters
      const isSameFilters = deepEquals(changeEvent, lastRunFilters);
      if (!isSameFilters) {
        console.log('Filters changed, resetting page number to 1');
        pageNumber.value = 1;
      }
    }
    lastRunFilters = changeEvent;
    paginationResult.value = await getPagination({
      taxonomies: changeEvent,
      pageNumber: pageNumber.value,
      pageLimit: pageLimit.value,
      contentTypes,
      sortDirection,
    });
  };

  const onTaxonomyChanged = (event: TaxonomyChangedPayload): void => {
    forceScroll.value = event.forceScroll ?? false;
    const prevState = taxonomyCheckedStates.value;
    taxonomyCheckedStates.value = {
      ...prevState,
      [event.slug]: event.isChecked,
    };
  };

  const checkedEventPayload: Ref<TaxonomyOptionsChangeEvent> = computed(() => {
    const checked: TaxonomyOptionsChangeEvent = {};
    for (const [slug, isChecked] of Object.entries(taxonomyCheckedStates.value)) {
      if (!isChecked) continue;
      const groupSlug = slugMap.get(slug)?.groupSlug;
      if (!groupSlug) {
        console.warn('No group slug found for slug', slug);
        continue;
      }
      checked[groupSlug] = checked[groupSlug] || [];
      checked[groupSlug].push(slug);

      console.warn('No parent or grandparent found for slug', slug);
    }

    let allSlugs: Array<string> = [];
    Object.entries(checked).forEach(([, slugs]) => {
      allSlugs = [...slugs, ...allSlugs];
    });
    allSlugs.sort();

    router.replace({
      path: router.currentRoute.value.path,
      query: {
        f: allSlugs,
        page: pageNumber.value > 1 ? String(pageNumber.value) : undefined,
      },
    });

    return checked;
  });

  const pillBarPills: Ref<Array<TaxonomyPill>> = computed(() => {
    const result: Array<TaxonomyPill> = [];

    Object.entries(taxonomyCheckedStates.value).forEach(([slug, isChecked]) => {
      if (!isChecked) return;

      const taxonomy = slugMap.get(slug);
      if (!taxonomy) {
        console.warn('No taxonomy found for slug', slug);
        return;
      }

      result.push({
        id: taxonomy.id,
        label: taxonomy.label,
        slug: taxonomy.slug,
      });
    });
    return result;
  });

  const runFilterNow = async () => {
    console.log('runFilterNow', checkedEventPayload.value);
    isFilterEventsQueued.value = false;
    try {
      isLoading.value = true;
      await runFilter(checkedEventPayload.value);
    } finally {
      if (forceScroll.value) {
        toTopOfWindow();
        forceScroll.value = false;
      }
      isLoading.value = false;
    }
  };

  const debouncedFilter = useDebounceFn(runFilterNow, 500);

  // handle the filter events
  const isFilterEventsQueued = ref(false);
  watch([checkedEventPayload], () => {
    isFilterEventsQueued.value = true;
    debouncedFilter();
  });

  const onPageChange = async (event: PageChangedPayload) => {
    console.log('onPageChange', event);

    if (isLoading.value) {
      console.warn('Already loading data');
      return;
    }

    // comment this line out to help when debugging pagination
    toTopOfWindow();

    try {
      pageNumber.value = event.pageNumber;
      paginationResult.value = await getPagination({
        taxonomies: checkedEventPayload.value,
        pageNumber: pageNumber.value,
        pageLimit: pageLimit.value,
        contentTypes,
        sortDirection,
      });
    } finally {
      isLoading.value = false;
    }
  };

  return {
    pageNumber,
    pageLimit,
    isLoading,
    isFilterEventsQueued,
    taxonomyOptionsConfig,
    paginationResult,
    taxonomyCheckedStates,
    pillBarPills,
    onPageChange,
    onTaxonomyChanged,
  };
};
